import { createPatch } from "diff";
import { diff_match_patch } from "diff-match-patch";
import { Diff2HtmlUI } from "diff2html/lib/ui/js/diff2html-ui-slim.js";

// taken from: //from: https://github.com/google/diff-match-patch/wiki/Line-or-Word-Diffs
export function diff_linesToWords(text1, text2, alsoSplitOnPunctuation = true) {
  var lineArray = []; // e.g. lineArray[4] == 'Hello'
  var lineHash = {}; // e.g. lineHash['Hello'] == 4

  // '\x00' is a valid character, but various debuggers don't like it.
  // So we'll insert a junk entry to avoid generating a null character.
  lineArray[0] = "";

  function diff_linesToWordsMunge_(text) {
    var chars = "";
    var lineStart = 0;
    var lineArrayLength = lineArray.length;

    // Split text into words while preserving newlines and all Unicode punctuation
    // If splitOnPunctuation = true is for the transcript user-facing realtime diff while editing, to make it produce an array of words on whitespace so it's compatible to the way the timestamp sync also produces an array of words (split on whitespace only).
    // if false, it's for making a nicer diff for manage review, where there's no current time highlight.
    const words = text.split(
      alsoSplitOnPunctuation ? /(\s|\n|[\p{P}\p{S}])/gu : /(\s|\n)/gu
    );

    for (let word of words) {
      // Skip empty strings
      if (word === "") continue;

      if (
        lineHash.hasOwnProperty
          ? lineHash.hasOwnProperty(word)
          : lineHash[word] !== undefined
      ) {
        chars += String.fromCharCode(lineHash[word]);
      } else {
        if (lineArrayLength == maxLines) {
          break;
        }
        chars += String.fromCharCode(lineArrayLength);
        lineHash[word] = lineArrayLength;
        lineArray[lineArrayLength++] = word;
      }
    }
    return chars;
  }

  var maxLines = 40000;
  var chars1 = diff_linesToWordsMunge_(text1);
  maxLines = 65535;
  var chars2 = diff_linesToWordsMunge_(text2);
  return { chars1: chars1, chars2: chars2, lineArray: lineArray };
}

export const Diff = {
  // Visible gap between the tooltip box and the hovered word, and the height of
  // the transparent hit-area "bridge" that spans that gap (slightly larger so
  // it overlaps the word and leaves no dead zone).
  REVERT_GAP: 8,
  REVERT_BRIDGE: 12,

  mounted() {
    this.createRevertButton();
    this.draw();
    this.handleEvent("redraw_diff", this.draw.bind(this));
  },

  updated() {
    this.draw();
  },

  destroyed() {
    clearTimeout(this._revertHideTimer);
    if (this.revertButton) {
      this.revertButton.remove();
      this.revertButton = null;
    }
  },

  draw() {
    const diffElement = this.el.querySelector(".diff-container");
    if (!diffElement) return;

    this.setupRevertListeners(diffElement);

    const { original, suggestion, title, view } = diffElement.dataset;

    const firstChild = diffElement.firstElementChild;
    if (firstChild) firstChild.classList.add("opacity-60");

    const generatingDiff = this.el.querySelector(".generating-diff");
    if (generatingDiff) generatingDiff.classList.remove("hidden");

    // Use setTimeout to defer the heavy computation to the next tick
    setTimeout(() => {
      // Clear the container's content, which also removes event listeners
      diffElement.innerHTML = "";

      if (view === "combined") {
        this.drawCombinedView(diffElement, original, suggestion);
      } else {
        this.drawSideBySideOrLineByLine(
          diffElement,
          original,
          suggestion,
          title,
          view
        );
      }

      if (this.el.classList.contains("line-edit-enabled")) {
        this.addLineClickListeners(diffElement);
      }
    }, 0);
  },

  drawCombinedView(diffElement, original, suggestion) {
    var diffMatchPatch = new diff_match_patch();
    diffMatchPatch.Diff_Timeout = 0;

    // Convert to word mode
    var a = diff_linesToWords(original, suggestion);
    var wordText1 = a.chars1;
    var wordText2 = a.chars2;
    var wordArray = a.lineArray;

    // Perform the diff
    var diff = diffMatchPatch.diff_main(wordText1, wordText2, false);

    // Convert back from word mode
    diffMatchPatch.diff_charsToLines_(diff, wordArray);

    // Keep the diff around so we can reconstruct text when reverting a change
    this.combinedDiff = diff;

    //diffMatchPatch.diff_cleanupSemantic(diff);
    //diffMatchPatch.diff_cleanupEfficiency(diff);
    const diffPrettyHtml = this.diffPrettyHtml(diff);
    diffElement.innerHTML = diffPrettyHtml;
    const generatingDiff = this.el.querySelector(".generating-diff");
    if (generatingDiff) generatingDiff.classList.add("hidden");
  },

  drawSideBySideOrLineByLine(
    diffElement,
    original,
    suggestion,
    title,
    outputFormat
  ) {
    const patch = createPatch(title, original, suggestion, null, null, {
      context: "Infinity",
    });

    const configuration = {
      drawFileList: false,
      fileListToggle: false,
      fileListStartVisible: false,
      fileContentToggle: false,
      matching: "lines",
      outputFormat,
      synchronisedScroll: true,
      highlight: true,
      renderNothingWhenEmpty: false,
    };

    const diff2htmlUi = new Diff2HtmlUI(diffElement, patch, configuration);
    diff2htmlUi.draw();
    const generatingDiff = this.el.querySelector(".generating-diff");
    if (generatingDiff) generatingDiff.classList.add("hidden");
  },

  addLineClickListeners(diffElement) {
    diffElement
      .querySelectorAll(".d2h-code-linenumber:not(.d2h-del)")
      .forEach((el) =>
        el.addEventListener("click", () =>
          this.handleLineClick(el, diffElement)
        )
      );
  },

  // Floating "Revert change" control, styled like the tooltip, shown above a
  // changed span on hover. Only relevant for the combined view.
  createRevertButton() {
    if (this.revertButton) return;

    const el = document.createElement("div");
    Object.assign(el.style, {
      position: "fixed",
      top: "0",
      left: "0",
      zIndex: "99999",
      display: "none",
      width: "max-content",
      // Transparent "bridge" below the visible box. It keeps the tooltip and
      // the hovered word connected so the mouse can travel straight up into the
      // tooltip without crossing a gap (or another change above it) that would
      // otherwise cause it to hide/switch.
      paddingBottom: `${this.REVERT_BRIDGE}px`,
    });
    el.innerHTML = `
      <div data-revert-box class="group relative bg-robe-950 rounded-md text-robe-25 text-[0.85rem] flex items-center gap-1 px-2 leading-4 pt-1.5 pb-2 cursor-pointer select-none whitespace-nowrap transition-colors duration-100 hover:bg-robe-800">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 shrink-0">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
        </svg>
        <span data-revert-label>Revert change</span>
        <div data-revert-arrow class="absolute left-1/2 -translate-x-1/2 -bottom-1 bg-robe-950 group-hover:bg-robe-800 transition-colors duration-100 w-2 h-2 rotate-45"></div>
      </div>
    `;
    document.body.appendChild(el);
    this.revertButton = el;
    this.revertButtonBox = el.querySelector("[data-revert-box]");
    this.revertButtonArrow = el.querySelector("[data-revert-arrow]");
    this.revertButtonLabel = el.querySelector("[data-revert-label]");

    el.addEventListener("mouseenter", () =>
      clearTimeout(this._revertHideTimer)
    );
    el.addEventListener("mouseleave", () => this.scheduleRevertHide());
    el.addEventListener("click", () => this.applyRevert());
  },

  scheduleRevertHide() {
    clearTimeout(this._revertHideTimer);
    this._revertHideTimer = setTimeout(() => this.hideRevertButton(), 120);
  },

  setupRevertListeners(diffElement) {
    if (this._boundContainer === diffElement) return;
    this._boundContainer = diffElement;

    diffElement.addEventListener("mouseover", (e) => {
      if (!this.el.classList.contains("line-edit-enabled")) return;
      if (diffElement.dataset.view !== "combined") return;
      const el = e.target.closest("[data-change-group]");
      if (!el || !diffElement.contains(el)) return;
      clearTimeout(this._revertHideTimer);
      this.showRevertButton(el, e);
    });

    diffElement.addEventListener("mouseout", (e) => {
      const el = e.target.closest("[data-change-group]");
      if (!el) return;
      const to = e.relatedTarget;
      if (
        to &&
        (to.closest("[data-change-group]") ||
          (this.revertButton && this.revertButton.contains(to)))
      ) {
        return;
      }
      this.scheduleRevertHide();
    });
  },

  showRevertButton(el, evt) {
    const gid = parseInt(el.dataset.changeGroup, 10);
    if (
      this.revertButton.style.display === "block" &&
      this.currentRevertGroup === gid
    ) {
      return;
    }
    this.currentRevertGroup = gid;

    // Dim the affected span so it's clear what will be acted on.
    this.setGroupHighlight(gid);

    // When the original text for this change is short, show what the result of
    // reverting will be; otherwise fall back to a generic label.
    const original = (this.revertGroupOriginalText(gid) || "").trim();
    this.revertButtonLabel.textContent =
      original.length > 0 && original.length < 20
        ? `Revert to "${original}"`
        : "Revert change";

    // Anchor the tooltip above the exact line of the word the user hovered on
    // (rather than the middle of a multi-line change). Inline elements can span
    // several line boxes, so pick the client rect under the pointer.
    const lineRects = [...el.getClientRects()].filter(
      (r) => r.width > 0 || r.height > 0
    );
    let anchor =
      lineRects[0] || el.getBoundingClientRect();
    if (evt && lineRects.length) {
      anchor =
        lineRects.find(
          (r) => evt.clientY >= r.top - 2 && evt.clientY <= r.bottom + 2
        ) || anchor;
    }

    // Horizontally track where the user hovered, clamped to the word's rect so
    // the tooltip stays over the word.
    const anchorX = evt
      ? Math.min(Math.max(evt.clientX, anchor.left), anchor.right)
      : (anchor.left + anchor.right) / 2;

    this.revertButton.style.display = "block";
    const boxRect = this.revertButtonBox.getBoundingClientRect();

    let left = anchorX - boxRect.width / 2;
    left = Math.max(
      4,
      Math.min(left, window.innerWidth - boxRect.width - 4)
    );
    // The visible box sits REVERT_GAP above the word; the container extends
    // down by REVERT_BRIDGE (transparent) to bridge that gap.
    const top = Math.max(4, anchor.top - this.REVERT_GAP - boxRect.height);
    this.revertButton.style.left = `${left}px`;
    this.revertButton.style.top = `${top}px`;

    // Point the arrow at the hovered word even when the box is clamped.
    let arrowLeft = anchorX - left;
    arrowLeft = Math.max(10, Math.min(arrowLeft, boxRect.width - 10));
    this.revertButtonArrow.style.left = `${arrowLeft}px`;
  },

  setGroupHighlight(gid) {
    this.clearGroupHighlight();
    if (!this._boundContainer) return;
    this._highlightedGroup = gid;
    this._boundContainer
      .querySelectorAll(`[data-change-group="${gid}"]`)
      .forEach((node) => node.classList.add("opacity-50"));
  },

  clearGroupHighlight() {
    if (this._boundContainer && this._highlightedGroup != null) {
      this._boundContainer
        .querySelectorAll(`[data-change-group="${this._highlightedGroup}"]`)
        .forEach((node) => node.classList.remove("opacity-50"));
    }
    this._highlightedGroup = null;
  },

  // The original text for a change group is its deleted content; reverting
  // restores it (and an insertion-only change reverts to nothing).
  revertGroupOriginalText(gid) {
    const diffs = this.combinedDiff;
    const segGroup = this.segGroup;
    if (!diffs || !segGroup) return "";

    let result = "";
    for (let i = 0; i < diffs.length; i++) {
      if (segGroup[i] === gid && diffs[i][0] === -1) {
        result += diffs[i][1];
      }
    }
    return result;
  },

  hideRevertButton() {
    this.clearGroupHighlight();
    if (this.revertButton) this.revertButton.style.display = "none";
    this.currentRevertGroup = null;
  },

  // Rebuild the suggestion text with the given change group reverted to the
  // original: drop insertions in that group, keep its deletions, and leave the
  // rest of the suggestion untouched.
  revertGroup(gid) {
    const diffs = this.combinedDiff;
    const segGroup = this.segGroup;
    if (!diffs || !segGroup) return null;

    let result = "";
    for (let i = 0; i < diffs.length; i++) {
      const [op, text] = diffs[i];
      const reverting = segGroup[i] === gid;
      if (op === 0) {
        result += text;
      } else if (op === 1) {
        if (!reverting) result += text;
      } else if (op === -1) {
        if (reverting) result += text;
      }
    }
    return result;
  },

  applyRevert() {
    if (this.currentRevertGroup == null) return;
    const newText = this.revertGroup(this.currentRevertGroup);
    if (newText == null) return;

    this.hideRevertButton();

    const diffElement = this._boundContainer;
    if (!diffElement) return;

    // Keep the dataset in sync so subsequent diffs/reverts use the new text,
    // persist the draft on the server, then re-render the combined view.
    diffElement.dataset.suggestion = newText;
    this.pushEvent("revert_change", { suggestion: newText });

    this.drawCombinedView(diffElement, diffElement.dataset.original, newText);
    if (this.el.classList.contains("line-edit-enabled")) {
      this.addLineClickListeners(diffElement);
    }
  },

  diffPrettyHtml(diffs) {
    let rows = [];
    let currentLine = "";
    let lineNumber = 0;
    let lineOperations = new Set();
    let hasLineBreak = false;

    // Assign each diff segment a "change group" id. A change group is a maximal
    // run of consecutive changed segments (insertions and/or deletions) bounded
    // by unchanged words. Reverting a group restores the original for that span.
    const segGroup = new Array(diffs.length).fill(null);
    let groupCounter = -1;
    let prevWasChange = false;
    for (let i = 0; i < diffs.length; i++) {
      if (diffs[i][0] !== 0) {
        if (!prevWasChange) groupCounter++;
        segGroup[i] = groupCounter;
        prevWasChange = true;
      } else {
        prevWasChange = false;
      }
    }
    this.segGroup = segGroup;

    const wrapInTag = (text, tag, gid) => {
      text = text ? text : "&nbsp;";
      if (!tag) return text;
      const attr = gid != null ? ` data-change-group="${gid}"` : "";
      return `<${tag}${attr}>${text}</${tag}>`;
    };

    const flushLine = () => {
      let tdClass = "";
      let showLineNumber = true;
      if (lineOperations.size > 1 && lineOperations.has(0)) {
        tdClass = "bg-robe-25!";
      } else if (lineOperations.has(-1)) {
        tdClass = "d2h-del";
      } else if (lineOperations.has(1)) {
        tdClass = "d2h-ins";
      }

      if (tdClass == "d2h-del") {
        showLineNumber = false;
      } else {
        lineNumber++;
      }

      if (currentLine) {
        rows.push(`
          <tr>
            <td class="d2h-code-linenumber d2h-cntx ${tdClass}">
              <div class="line-num2">${showLineNumber ? lineNumber : ""}</div>
            </td>
            <td class="d2h-cntx ${tdClass}">
              <div class="d2h-code-line d2h-code-line-combined">
                <div class="d2h-code-line-ctn hljs plaintext">${currentLine}</div>
              </div>
            </td>
          </tr>
        `);
        currentLine = "";
        lineOperations.clear();
      }
    };

    for (let x = 0; x < diffs.length; x++) {
      const [operation, text] = diffs[x];

      switch (operation) {
        case 1:
          tag = "ins";
          break;
        case -1:
          tag = "del";
          break;
        default:
          tag = "";
      }

      if (operation === -1) {
        lineOperations.add(operation);
        currentLine += wrapInTag(text, tag, segGroup[x]);
        if (hasLineBreak) {
          flushLine();
        }
      } else {
        const lines = text.split(/(\n)/).filter(Boolean);

        lines.forEach((line, index) => {
          if (!line) {
            return;
          }

          let tag;
          switch (operation) {
            case 1:
              tag = "ins";
              break;
            case -1:
              tag = "del";
              break;
            default:
              tag = "";
          }

          lineOperations.add(operation);
          currentLine += wrapInTag(line, tag, segGroup[x]);

          hasLineBreak = /\r|\n/.exec(line);

          if (hasLineBreak) {
            flushLine();
          }
        });
      }
    }

    flushLine(); // Flush any remaining content

    return `
      <div class="d2h-wrapper d2h-light-color-scheme">
        <div class="d2h-file-wrapper">
          <div class="d2h-file-diff">
            <div class="d2h-code-wrapper">
              <table class="d2h-diff-table">
                <tbody class="d2h-diff-tbody">${rows.join("\n")}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    `;
  },
  handleLineClick(el, diffElement) {
    // Cancel other editing items
    diffElement.querySelectorAll("tr.editing").forEach((item) => {
      const codeLine = item.querySelector(".d2h-code-line-ctn");
      if (codeLine && codeLine.dataset.originalHtml) {
        codeLine.innerHTML = codeLine.dataset.originalHtml;
        item.classList.remove("editing");
      }
    });

    const codeLine = el.nextElementSibling.querySelector(".d2h-code-line-ctn");
    if (!el.parentElement.classList.contains("editing") && codeLine) {
      el.parentElement.classList.add("editing");
      codeLine.dataset.originalHtml = codeLine.innerHTML;
      const dels = codeLine.querySelectorAll("del");
      dels.forEach((del) => del.remove());
      const originalContent = codeLine.innerText.trim();

      const container = document.createElement("div");
      container.classList.add("edit-container", "flex", "flex-wrap", "py-3");

      const textarea = document.createElement("textarea");
      textarea.value = originalContent;
      textarea.rows = 10;
      textarea.style.height = `${codeLine.offsetHeight + 20}px`;
      textarea.classList.add(
        "block",
        "w-full",
        "rounded-lg",
        "text-robe-900",
        "focus:ring-0",
        "phx-no-feedback:border-robe-300",
        "focus:phx-no-feedback:border-robe-400",
        "border-robe-300",
        "focus:border-robe-400",
        "text-[13px]",
        "leading-5",
        "mb-2"
      );

      const buttonClasses = [
        "rounded-sm",
        "text-sm",
        "py-1",
        "px-3",
        "font-sans",
        "font-bold",
        "cursor-pointer",
      ];

      const cancelButton = document.createElement("button");
      cancelButton.textContent = "Cancel";
      cancelButton.classList.add(
        ...buttonClasses,
        "bg-black",
        "text-white",
        "mr-2"
      );
      cancelButton.onclick = () => this.cleanup(codeLine);

      const saveButton = document.createElement("button");
      saveButton.textContent = "Save";
      saveButton.classList.add(...buttonClasses, "bg-sun-700", "text-black");
      saveButton.onclick = () => {
        const lineNumber = this.getLineNumber(codeLine);
        const currentContent = textarea.value;
        this.pushEvent("update_suggestion", { lineNumber, currentContent });
        this.cleanup(codeLine);
      };

      container.append(textarea, cancelButton, saveButton);
      codeLine.innerHTML = "";
      codeLine.appendChild(container);
    }
  },

  cleanup(codeLine) {
    codeLine.innerHTML = codeLine.dataset.originalHtml;
    codeLine.closest("tr").classList.remove("editing");
  },

  getLineNumber(codeLine) {
    const lineNumElement = codeLine.closest("tr").querySelector(".line-num2");
    return (lineNumElement?.textContent || "").trim().replace(/\D/g, "");
  },
};
